import React, { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { leaveForTp1SignIn, usesTp1SignIn } from '~libs/signInRoute';

import FullPageLoader from '~components/FullPageLoader';
import SessionExpiryOverlay from '~main/components/SessionExpiryOverlay';
import SessionReauthModal from '~main/components/SessionReauthModal';
import SessionUnavailable from '~main/components/SessionUnavailable';

import { getSettings } from '~main/actions/login';
import { bootstrapSession } from '~main/actions/session';

import { selectAuthenticated, selectSessionStatus, selectSignOutReason, selectUserTags } from '~main/selectors/login';

// Single-use handoff ticket minted by tp-v1's tav-go.asp. The legacy tp1-session
// parameter stays accepted until TP1 stops emitting it.
const HANDOFF_PARAM = 'tav-handoff';
const LEGACY_SESSION_PARAM = 'tp1-session';
// TP1 adds sso=1 to a handoff when this browser signed in to TP1 through SSO. A UI hint only: it picks which
// option the session-limit prompt shows, and carries no authority.
const SSO_PARAM = 'sso';

const PrivateRoute: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handoffKey = searchParams.get(HANDOFF_PARAM);
  const legacySessionKey = searchParams.get(LEGACY_SESSION_PARAM);
  const crossAppKey = handoffKey ?? legacySessionKey;
  const isSso = searchParams.get(SSO_PARAM) === '1';

  // A parameter present with no value reads as '' rather than null, which is falsy. Presence
  // is what matters, not emptiness: the key still arrived from TP1, so it belongs on the
  // redeem path where the endpoint rejects it like any other bad value. Treating it as absent
  // instead would skip redemption entirely and leave the empty parameter in the address bar.
  const isHandoff = handoffKey !== null;
  const hasCrossAppKey = isHandoff || legacySessionKey !== null;

  const isAuthenticated = useAppSelector(selectAuthenticated);
  const sessionStatus = useAppSelector(selectSessionStatus);
  const tags = useAppSelector(selectUserTags);
  const signOutReason = useAppSelector(selectSignOutReason);
  const dispatch = useAppDispatch();

  const tagsLoading = !tags || tags.length === 0;
  const showOutlet = isAuthenticated && !hasCrossAppKey && !tagsLoading;

  // Settings are requested once per mount, whether the session was already known or only just confirmed.
  const settingsRequested = useRef(false);
  const loadSettings = () => {
    if (settingsRequested.current) return;
    settingsRequested.current = true;
    dispatch(getSettings());
  };

  useEffect(() => {
    // If a cross-application key is present, redirect to check it via component
    if (hasCrossAppKey) {
      // Strip both forms and the SSO hint, so neither survives into the route the user lands on.
      searchParams.delete(HANDOFF_PARAM);
      searchParams.delete(LEGACY_SESSION_PARAM);
      searchParams.delete(SSO_PARAM);
      const searchParamString = searchParams.toString();
      const fromUrl = location.pathname + (searchParamString ? `?${searchParamString}` : '');
      navigate('/auth-session', {
        state: { from: fromUrl, sessionKey: crossAppKey, isHandoff, isSso },
      });
    } else if (isAuthenticated) {
      loadSettings();
    } else if (sessionStatus === 'unknown') {
      // A page load knows nothing yet: ask the API whether the cookie session is alive before deciding.
      dispatch(bootstrapSession());
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && !hasCrossAppKey) loadSettings();
  }, [isAuthenticated]);

  useEffect(() => {
    // Only a definite "no session" redirects. While the status is unknown the loader shows instead, which
    // is what stops a reload bouncing a signed-in user to /login before the check has answered.
    if (sessionStatus === 'none' && !isAuthenticated && !hasCrossAppKey) {
      if (!usesTp1SignIn()) {
        navigate('/login', { state: { from: location.pathname } });
      } else if (signOutReason === 'signed-out') {
        // Another tab signed out on purpose: show the signed-out card rather than bouncing through TP1.
        navigate('/logout', { replace: true, state: { signedOut: true } });
      } else if (!leaveForTp1SignIn()) {
        navigate('/login', { state: { from: location.pathname } });
      }
    }
  }, [isAuthenticated, sessionStatus]);

  return (
    <>
      {sessionStatus === 'unavailable' && !isAuthenticated && !hasCrossAppKey ? (
        <SessionUnavailable />
      ) : (
        <FullPageLoader isLoading={tagsLoading || sessionStatus === 'unknown'} />
      )}
      {showOutlet && <Outlet />}
      {isAuthenticated && (
        <>
          <SessionExpiryOverlay />
          <SessionReauthModal />
        </>
      )}
    </>
  );
};

export default PrivateRoute;
